import { FetchError } from '../errors/FetchError'
import { fetchFileWithAuthorization, fetchWithAuthorization } from './fetch'
import {
  mockAccelerators,
  mockConfig,
  mockConfigAudits,
  mockCreateAccelerator,
  mockCreatePartner,
  mockDeleteAccelerator,
  mockDeletePartner,
  mockExportReport,
  mockPartners,
  mockReport,
  mockRotatePartnerSecret,
  mockRuleHistory,
  mockSaveRule,
  mockSimulate,
  mockUpdateAccelerator,
  mockUpdateLevel,
  mockUpdatePartner
} from '../../components/root/rewards/mocks/rewardsAdminMock'

// HO-TASK-289: panel de root de Homelan Rewards. Contrato REAL del backend,
// todo bajo /rewards con rol root (routes/api.php):
//   - configuración (HO-TASK-276): RewardConfigController
//   - reporte financiero y de auditoría (HO-TASK-277): RewardReportController
//   - aliados y aceleradores: RewardPartnerController y RewardAcceleratorController
// Igual que el resto de Rewards, en desarrollo responde con datos simulados de
// la misma forma salvo que VITE_REWARDS_MOCK=false; en el build nunca.
const USE_REWARDS_MOCK = import.meta.env.DEV && import.meta.env.VITE_REWARDS_MOCK !== 'false'

const request = async (url, message, body = null, method = 'GET') => {
  try {
    return await fetchWithAuthorization(url, body, method)
  } catch (error) {
    throw new FetchError(error, message)
  }
}

const buildQuery = (params) => {
  const query = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') query.append(key, value)
  })
  const text = query.toString()
  return text ? `?${text}` : ''
}

// --- Reporte (HO-TASK-277) ---

export const getRewardsReport = async ({ startDate, endDate, condominiumId }) => {
  const params = { start_date: startDate, end_date: endDate, condominium_id: condominiumId }
  if (USE_REWARDS_MOCK) return mockReport(params)

  return request(`/rewards/report${buildQuery(params)}`, 'Ha ocurrido un error al consultar el reporte de Homelan Rewards.')
}

// Devuelve el archivo como Blob. `format`: xlsx, csv o txt (SpreadsheetDownload).
export const exportRewardsReport = async ({ startDate, endDate, condominiumId, format }) => {
  const params = { start_date: startDate, end_date: endDate, condominium_id: condominiumId, format }
  if (USE_REWARDS_MOCK) return mockExportReport(params)

  try {
    return await fetchFileWithAuthorization(`/rewards/report/export${buildQuery(params)}`)
  } catch (error) {
    throw new FetchError(error, 'No pudimos descargar el reporte de Homelan Rewards.')
  }
}

// --- Configuración (HO-TASK-276) ---

export const getRewardsConfig = async () => {
  if (USE_REWARDS_MOCK) return mockConfig()
  return request('/rewards/config', 'Ha ocurrido un error al consultar la configuración de Homelan Rewards.')
}

// Crea una versión nueva del parámetro vigente desde `validFrom`; la anterior
// se cierra el día antes. 422 con `error` si deja el margen en cero o negativo,
// si está fuera de rango o si ya hay una versión desde esa fecha.
export const saveRewardRule = async ({ key, value, validFrom, note }) => {
  const body = { key, value, valid_from: validFrom || null, note: note || null }
  if (USE_REWARDS_MOCK) return mockSaveRule(body)
  return request('/rewards/config', 'No pudimos guardar el parámetro.', body, 'POST')
}

export const getRewardRuleHistory = async (key) => {
  if (USE_REWARDS_MOCK) return mockRuleHistory(key)
  return request(`/rewards/config/${encodeURIComponent(key)}/history`, 'Ha ocurrido un error al consultar el historial del parámetro.')
}

export const getRewardConfigAudits = async (key, limit = 50) => {
  if (USE_REWARDS_MOCK) return mockConfigAudits(key, limit)
  return request(`/rewards/config/audits${buildQuery({ key, limit })}`, 'Ha ocurrido un error al consultar la bitácora de cambios.')
}

export const updateRewardLevel = async (levelId, { minXp, maxXp, coinMultiplier, note }) => {
  const body = { min_xp: minXp, max_xp: maxXp, coin_multiplier: coinMultiplier, note: note || null }
  if (USE_REWARDS_MOCK) return mockUpdateLevel(levelId, body)
  return request(`/rewards/config/levels/${levelId}`, 'No pudimos guardar el nivel.', body, 'PUT')
}

// Proyección de costo y margen para un volumen de usuarios. `overrides`
// ({ clave: valor }) simula un cambio antes de guardarlo; no guarda nada.
export const simulateRewardsProgram = async ({ users, averageFeeUsd, overrides }) => {
  const body = { users, average_fee_usd: averageFeeUsd, overrides: overrides || {} }
  if (USE_REWARDS_MOCK) return mockSimulate(body)
  return request('/rewards/config/simulate', 'No pudimos calcular la proyección.', body, 'POST')
}

// --- Aliados y aceleradores ---

export const getRewardPartners = async () => {
  if (USE_REWARDS_MOCK) return mockPartners()
  return request('/rewards/partners', 'Ha ocurrido un error al consultar los aliados.')
}

// La respuesta trae `webhook_secret`: es la única vez que se ve, además de al rotarlo.
export const createRewardPartner = async (partner) => {
  if (USE_REWARDS_MOCK) return mockCreatePartner(partner)
  return request('/rewards/partners', 'No pudimos crear el aliado.', partner, 'POST')
}

export const updateRewardPartner = async (partnerId, partner) => {
  if (USE_REWARDS_MOCK) return mockUpdatePartner(partnerId, partner)
  return request(`/rewards/partners/${partnerId}`, 'No pudimos guardar el aliado.', partner, 'PUT')
}

export const rotateRewardPartnerSecret = async (partnerId) => {
  if (USE_REWARDS_MOCK) return mockRotatePartnerSecret(partnerId)
  return request(`/rewards/partners/${partnerId}/secret`, 'No pudimos generar un secreto nuevo.', null, 'PUT')
}

export const deleteRewardPartner = async (partnerId) => {
  if (USE_REWARDS_MOCK) return mockDeletePartner(partnerId)
  return request(`/rewards/partners/${partnerId}`, 'No pudimos eliminar el aliado.', null, 'DELETE')
}

export const getRewardAccelerators = async () => {
  if (USE_REWARDS_MOCK) return mockAccelerators()
  return request('/rewards/accelerators/all', 'Ha ocurrido un error al consultar los aceleradores.')
}

export const createRewardAccelerator = async (accelerator) => {
  if (USE_REWARDS_MOCK) return mockCreateAccelerator(accelerator)
  return request('/rewards/accelerators', 'No pudimos crear el acelerador.', accelerator, 'POST')
}

export const updateRewardAccelerator = async (acceleratorId, accelerator) => {
  if (USE_REWARDS_MOCK) return mockUpdateAccelerator(acceleratorId, accelerator)
  return request(`/rewards/accelerators/${acceleratorId}`, 'No pudimos guardar el acelerador.', accelerator, 'PUT')
}

// El backend no lo borra: lo deja inactivo.
export const deactivateRewardAccelerator = async (acceleratorId) => {
  if (USE_REWARDS_MOCK) return mockDeleteAccelerator(acceleratorId)
  return request(`/rewards/accelerators/${acceleratorId}`, 'No pudimos desactivar el acelerador.', null, 'DELETE')
}

// Mensaje de error de las acciones de este panel: 422 trae `error` en español
// (RewardLedgerException), 406 trae `errors` por campo.
export const getRewardsAdminErrorMessage = (error, fallback) => {
  const data = error?.data || error
  if (data?.error) return data.error
  const fieldErrors = data?.errors && Object.values(data.errors).flat()
  if (fieldErrors?.length) return fieldErrors[0]
  return fallback || error?.message || 'Ha ocurrido un error inesperado. Intenta de nuevo.'
}
